import { describeSheetFailure } from './sheetFailure';
import { WalletCancelledError } from './walletErrors';
import type { ApplePaySheetConfig, Location } from '@/api/types';

/**
 * The Apple Pay sheet, opened by the tap that asked for it.
 *
 * WHY THIS IS NOT SIMPLY A FUNCTION CALL. Safari refuses to construct an `ApplePaySession` outside a user
 * gesture handler — "Must create a new ApplePaySession from a user gesture handler" — and it means the
 * synchronous part of that handler, before anything is awaited. The app used to build the sheet from the
 * response to `POST /payments`, 160–290ms and two awaits after the tap, so the constructor threw every time,
 * before Apple was ever contacted. No Apple Pay sheet had ever opened on this path.
 *
 * So construction happens at the tap and the rest of the flow catches up afterwards. The sheet outlives a
 * navigation and a network round trip on its way to the code that knows what to do with a token, which is why
 * it lives at module scope rather than in a component: React state does not survive a route change, and there
 * is nothing else both the tip screen and the payment screen can hold.
 *
 * Everything Apple sends between `begin()` and the handlers arriving is QUEUED rather than dropped.
 * `onvalidatemerchant` normally fires while the guest is still looking at the sheet, but "normally" is not a
 * guarantee worth a failed payment, and a lost validation event is a modal that never resolves.
 */

const API_VERSION = 3;

interface ApplePayValidateEvent {
    validationURL: string;
}

interface ApplePayAuthorizedEvent {
    payment: Record<string, unknown>;
}

interface ApplePaySessionInstance {
    onvalidatemerchant: ((event: ApplePayValidateEvent) => void) | null;
    onpaymentauthorized: ((event: ApplePayAuthorizedEvent) => void) | null;
    oncancel: (() => void) | null;
    begin(): void;
    abort(): void;
    completeMerchantValidation(merchantSession: unknown): void;
    completePayment(result: { status: number }): void;
}

interface ApplePaySessionConstructor {
    new (version: number, request: unknown): ApplePaySessionInstance;
    canMakePayments?: () => boolean;
    supportsVersion?: (version: number) => boolean;
    STATUS_SUCCESS: number;
    STATUS_FAILURE: number;
}

export interface ApplePayHandlers {
    /** Exchanges Apple's validation URL for a merchant session, via our server. */
    validateMerchant: (validationUrl: string) => Promise<unknown>;

    /**
     * Sends the authorised token on and reports whether it actually settled.
     *
     * Awaited before the sheet is dismissed, so the tick or cross a guest sees is the real outcome rather
     * than an optimistic guess. Apple allows around thirty seconds for this.
     */
    authorize: (payment: Record<string, unknown>) => Promise<boolean>;
}

function sessionConstructor(): ApplePaySessionConstructor {
    const constructor = (window as { ApplePaySession?: unknown }).ApplePaySession;
    if (!constructor) throw new Error('Apple Pay is not available in this browser.');

    return constructor as ApplePaySessionConstructor;
}

/**
 * The request the sheet renders.
 *
 * Every field but the amount comes from the location, which the client already holds before the guest taps.
 * The amount is the total already on the screen they tapped from — and the server still settles the charge
 * from the payment record, refusing any payment whose expected total disagrees with the check, so a client
 * showing the wrong number cannot charge it.
 */
export function paymentRequest(sheet: ApplePaySheetConfig, totalAmount: string) {
    return {
        countryCode: sheet.countryCode,
        currencyCode: sheet.currencyCode,
        supportedNetworks: sheet.supportedNetworks,
        merchantCapabilities: sheet.merchantCapabilities,
        total: {
            label: sheet.totalLabel,
            amount: totalAmount,
            type: 'final',
        },
    };
}

/** Minor units to the decimal string Apple's sheet wants. Never localised — Apple parses this. */
export function majorUnits(minorUnits: number): string {
    return (minorUnits / 100).toFixed(2);
}

interface Pending {
    session: ApplePaySessionInstance;
    constructor: ApplePaySessionConstructor;
    /** Events Apple sent before anything was listening. Replayed in order once handlers arrive. */
    queue: (() => void)[];
    handlers: ApplePayHandlers | null;
    settle: { resolve: () => void; reject: (error: unknown) => void } | null;
}

let pending: Pending | null = null;

export type SheetStart = { started: true } | { started: false; detail: string };

/**
 * Constructs the sheet and opens it. Must be called synchronously from the tap.
 *
 * Returns rather than throws, because the caller is a click handler on its way to a navigation, and an
 * exception there loses both the sheet and the reason for it. `detail` is what the failure screen shows
 * under `?debug=1`.
 */
export function startApplePaySheet(sheet: ApplePaySheetConfig, totalAmount: string): SheetStart {
    discardApplePaySheet();

    try {
        const ApplePaySession = sessionConstructor();
        const session = new ApplePaySession(API_VERSION, paymentRequest(sheet, totalAmount));

        const state: Pending = {
            session,
            constructor: ApplePaySession,
            queue: [],
            handlers: null,
            settle: null,
        };

        // Attached before `begin()`, never after: Apple may call back the moment the sheet appears, and a
        // handler assigned on the following line is already too late to be certain of.
        session.onvalidatemerchant = (event) => enqueue(state, () => void validate(state, event.validationURL));
        session.onpaymentauthorized = (event) => enqueue(state, () => void authorize(state, event.payment));
        session.oncancel = () =>
            enqueue(state, () => finish(state, { ok: false, error: new WalletCancelledError() }));

        pending = state;
        session.begin();

        return { started: true };
    } catch (error) {
        pending = null;

        return { started: false, detail: describeSheetFailure(error) ?? 'The Apple Pay sheet would not open.' };
    }
}

function enqueue(state: Pending, run: () => void): void {
    if (state.handlers) run();
    else state.queue.push(run);
}

async function validate(state: Pending, validationUrl: string): Promise<void> {
    try {
        const merchantSession = await state.handlers!.validateMerchant(validationUrl);
        state.session.completeMerchantValidation(merchantSession);
    } catch (error) {
        // Aborted rather than left open. A guest staring at a spinner that will never resolve is worse than
        // one told it did not work.
        state.session.abort();
        finish(state, { ok: false, error });
    }
}

async function authorize(state: Pending, payment: Record<string, unknown>): Promise<void> {
    try {
        const settled = await state.handlers!.authorize(payment);

        state.session.completePayment({
            status: settled ? state.constructor.STATUS_SUCCESS : state.constructor.STATUS_FAILURE,
        });

        if (settled) finish(state, { ok: true });
        else finish(state, { ok: false, error: new Error('The payment was not accepted.') });
    } catch (error) {
        state.session.completePayment({ status: state.constructor.STATUS_FAILURE });
        finish(state, { ok: false, error });
    }
}

/**
 * The sheet's outcome, delivered to whoever attached.
 *
 * `state.settle` is always set by the time this runs, and that is a property of `enqueue` rather than an
 * accident: every path here goes through it, and it queues until handlers arrive. An earlier draft also kept
 * the outcome in a `settled` field for a caller that had not attached yet — unreachable, and a safety net
 * that cannot fire is worse than none, because it reads as though the case is covered.
 */
function finish(state: Pending, outcome: { ok: true } | { ok: false; error: unknown }): void {
    if (outcome.ok) state.settle?.resolve();
    else state.settle?.reject(outcome.error);

    if (pending === state) pending = null;
}

export function hasPendingApplePaySheet(): boolean {
    return pending !== null;
}

/**
 * Takes over a sheet the tap already opened.
 *
 * Resolves once the payment is authorised and settled, rejects with `WalletCancelledError` if the guest
 * closed it, and rejects normally if anything broke. Null when there is no sheet to take over — a recovery
 * route, a reload, a guest who reached the payment step without tapping anything.
 */
export function attachApplePaySheet(handlers: ApplePayHandlers): Promise<void> | null {
    const state = pending;
    if (!state) return null;

    state.handlers = handlers;

    return new Promise<void>((resolve, reject) => {
        state.settle = { resolve, reject };

        // Anything Apple sent while the app was still navigating. Replayed here, inside the executor, so the
        // promise it settles already exists.
        const queued = state.queue.splice(0);
        for (const run of queued) run();
    });
}

/** Drops a sheet nobody is going to drive, so the next tap does not inherit it. */
export function discardApplePaySheet(): void {
    if (!pending) return;

    const state = pending;
    pending = null;

    try {
        state.session.abort();
    } catch {
        // Already finished, or never began. Either way there is nothing to clean up.
    }
}


/**
 * Why the last attempt to open a sheet did not, kept for the screen that eventually reports it.
 *
 * The tap that starts a sheet is on its way to a navigation and has nowhere to show anything. By the time
 * there is a screen to report on, the failure is two routes behind — so it is written down here instead of
 * being lost, and `PaymentActionHost` reads it when it finds no sheet to attach to.
 */
let lastFailure: string | null = null;

export function lastSheetFailure(): string | null {
    return lastFailure;
}

/**
 * Opens the sheet for a location and a total, from inside the tap.
 *
 * The total is the one already on the screen the guest tapped from. The charge is still settled server-side
 * from the payment record, and `BeginPayment` refuses a payment whose expected total disagrees with the
 * check — so this number is what the sheet displays, never what it charges.
 */
export function startApplePaySheetFor(location: Location, totalCents: number): SheetStart {
    const sheet = location.digitalWalletConfiguration?.applePaySheet ?? null;

    const outcome: SheetStart = sheet
        ? startApplePaySheet(sheet, majorUnits(totalCents))
        : {
              started: false,
              detail: 'This location sent no Apple Pay sheet configuration, so no sheet could be built.',
          };

    lastFailure = outcome.started ? null : outcome.detail;

    return outcome;
}
